// Registry of gesture TOOLS for the voxel editor's trigger-press dispatch.
// A tool owns one branch of what used to be main's applyToolAction (start) and
// finalizeInteraction (commit) chains:
//
//   • startFor(i): the first registered tool whose claims() matches the current
//     UI state begins the gesture — called inside the already-open grid
//     transaction, exactly where its old applyToolAction branch ran.
//   • commitFor(kind, i): routes gesture-end work by the LIVE interaction kind,
//     not by who started it (e.g. the Select tool also starts move_selection
//     drags — the Move tool's commit finalizes those too, as the old kind-keyed
//     if/else chain did). Returns false when no tool owns the kind, so main's
//     legacy chain can handle the not-yet-migrated kinds (strangler seam).
//
// A tool's commit must verify interaction.controllerIndex itself (the legacy
// branches did) and no-op on a mismatch — "owned but declined" still counts as
// handled, matching the old chain's fall-through-to-nothing behaviour.

import { type Interaction } from "../interaction/types";

export type InteractionKind = Interaction["kind"];

export interface GestureTool {
  id: string;
  claims(): boolean; // does this tool own the current trigger press?
  start(i: number): void; // begin the gesture (transaction already open)
  kinds?: readonly InteractionKind[]; // interaction kinds this tool finalizes
  commit?(i: number): void; // gesture-end work for an owned kind
}

export interface ToolRegistry {
  register(tool: GestureTool): void;
  startFor(i: number): boolean; // true if a registered tool claimed the press
  commitFor(kind: InteractionKind, i: number): boolean; // true if a tool owns `kind`
}

export function createToolRegistry(): ToolRegistry {
  const tools: GestureTool[] = [];
  return {
    register(tool): void {
      tools.push(tool);
    },
    startFor(i): boolean {
      for (const t of tools) {
        if (t.claims()) {
          t.start(i);
          return true;
        }
      }
      return false;
    },
    commitFor(kind, i): boolean {
      for (const t of tools) {
        if (t.kinds?.includes(kind)) {
          t.commit?.(i);
          return true;
        }
      }
      return false;
    },
  };
}
